import moment from "moment"

// ---------------------------------------------------------------------------
// Array helpers
// ---------------------------------------------------------------------------

function sum(arr) {
  if (!arr || !arr.length) return 0
  return arr.reduce((a, b) => a + b, 0)
}

// ---------------------------------------------------------------------------
// Session stats builder
// ---------------------------------------------------------------------------

/**
 * Build denormalized session-stats series from raw period stats.
 * Pure function — no Vuex dependency.
 *
 * @param {Array} stats  Raw period_stats or hour_stats from the API
 * @param {Boolean} isDayPeriod  true when viewing a single day (hourly data)
 * @returns {Object} Series arrays keyed by metric name
 */
export function buildSessionStats(stats, isDayPeriod) {
  const result = {
    timePeriod: [],
    connections: [],
    uniques: [],
    listeningTime: [],
    listeningTimeMinutes: [],
    listeningTimePC: [],
    connected: [],
    disconnected: [],
    streams: [],
    impressions: [],
    thirdQuartile: [],
  }

  if (!stats || !stats.length) return result

  for (let i = 0; i < stats.length; i++) {
    const entry = stats[i]

    if (isDayPeriod) {
      result.timePeriod.push(("0" + entry.hour).slice(-2) + ":00")
    } else {
      result.timePeriod.push(entry.day)
    }

    if (entry.stats.connections !== undefined) {
      result.connections.push(entry.stats.connections)
    }

    if (entry.stats.uniques !== undefined) {
      result.uniques.push(entry.stats.uniques)
    }

    if (entry.stats.listening_time !== undefined) {
      result.listeningTime.push(entry.stats.listening_time)
      result.listeningTimeMinutes.push(
        moment.duration(entry.stats.listening_time, "seconds").asMinutes().toFixed(2),
      )
    }

    if (entry.stats.connected !== undefined) {
      result.connected.push(entry.stats.connected)
    }

    if (entry.stats.disconnected !== undefined) {
      result.disconnected.push(entry.stats.disconnected)
    }

    if (entry.stats.impressions !== undefined) {
      result.impressions.push(entry.stats.impressions)
    }

    if (entry.stats.thirdQuartile !== undefined) {
      result.thirdQuartile.push(entry.stats.thirdQuartile)
    }

    if (entry.stats.connections !== undefined) {
      let duration = 0
      if (entry.stats.connections > 0) {
        duration = moment
          .duration(entry.stats.listening_time / entry.stats.connections, "seconds")
          .asMinutes()
          .toFixed(2)
      }
      result.listeningTimePC.push(parseFloat(duration))
    }
  }

  return result
}

// ---------------------------------------------------------------------------
// Totals computation
// ---------------------------------------------------------------------------

/**
 * Compute aggregate totals from a session-stats series object.
 *
 * @param {Object} session  Output of buildSessionStats()
 * @returns {Object} Flat totals object
 */
export function computeTotals(session) {
  if (!session) {
    return emptyTotals()
  }

  const totalConnections = sum(session.connections)
  const totalDisconnections = sum(session.disconnected)
  const totalConnected = sum(session.connected)
  const totalUniques = sum(session.uniques)
  const totalThirdQuartile = sum(session.thirdQuartile)
  const totalImpressions = sum(session.impressions)
  const totalListeningTimeSec = sum(session.listeningTime)

  const totalListeningTimeHours = totalListeningTimeSec
    ? +moment.duration(totalListeningTimeSec, "seconds").asHours().toFixed(2)
    : 0

  const totalListeningTimeMinutes = totalListeningTimeSec
    ? +moment.duration(totalListeningTimeSec, "seconds").asMinutes().toFixed(2)
    : 0

  const averageListeningTimeMinutes =
    totalListeningTimeSec && totalConnections
      ? +moment.duration(totalListeningTimeSec / totalConnections, "seconds").asMinutes().toFixed(2)
      : 0

  const averageConnections =
    session.connections.length
      ? +(totalConnections / session.connections.length).toFixed(2)
      : 0

  const averageDisconnections =
    session.disconnected.length
      ? +(totalDisconnections / session.disconnected.length).toFixed(2)
      : 0

  const averageConnected =
    session.connected.length
      ? +(totalConnected / session.connected.length).toFixed(2)
      : 0

  const averageCallingTimeMinutes =
    totalListeningTimeSec && totalConnected
      ? +moment.duration(totalListeningTimeSec / totalConnected, "seconds").asMinutes().toFixed(2)
      : 0

  const totalCallingTimeMinutes = totalListeningTimeSec
    ? +moment.duration(totalListeningTimeSec, "seconds").asMinutes().toFixed(2)
    : 0

  return {
    connections: totalConnections,
    disconnections: totalDisconnections,
    connected: totalConnected,
    uniques: totalUniques,
    thirdQuartile: totalThirdQuartile,
    impressions: totalImpressions,
    listeningTimeSec: totalListeningTimeSec,
    listeningTimeHours: totalListeningTimeHours,
    listeningTimeMinutes: totalListeningTimeMinutes,
    averageListeningTimeMinutes,
    averageConnections,
    averageDisconnections,
    averageConnected,
    averageCallingTimeMinutes,
    callingTimeMinutes: totalCallingTimeMinutes,
  }
}

function emptyTotals() {
  return {
    connections: 0,
    disconnections: 0,
    connected: 0,
    uniques: 0,
    thirdQuartile: 0,
    impressions: 0,
    listeningTimeSec: 0,
    listeningTimeHours: 0,
    listeningTimeMinutes: 0,
    averageListeningTimeMinutes: 0,
    averageConnections: 0,
    averageDisconnections: 0,
    averageConnected: 0,
    averageCallingTimeMinutes: 0,
    callingTimeMinutes: 0,
  }
}

// ---------------------------------------------------------------------------
// Comparison computation (delegates to shared helper)
// ---------------------------------------------------------------------------

export { computeComparison } from "@/common/helpers/comparisonMetrics.helper"

